import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useProject } from "@/lib/portal/useProject";
import { Plus, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/upgrades")({
  component: UpgradesPage,
});

function UpgradesPage() {
  const qc = useQueryClient();
  const { data: project } = useProject();
  const [reqOpen, setReqOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");

  const { data: addons } = useQuery({
    queryKey: ["portal", "addons"],
    queryFn: async () => (await supabase.from("addon_catalog").select("*").eq("is_active", true).order("sort_order")).data ?? [],
  });
  const { data: requests } = useQuery({
    queryKey: ["portal", "feature-requests"],
    queryFn: async () => (await supabase.from("feature_requests").select("*").order("created_at", { ascending: false })).data ?? [],
    enabled: !!project,
  });

  const addAddon = async (name: string) => {
    if (!project) return;
    const { data: user } = await supabase.auth.getUser();
    await supabase.from("feature_requests").insert({
      project_id: project.id, requested_by: user.user!.id,
      title: `Aggiungi al sito: ${name}`, status: "requested",
    });
    toast.success("Richiesta inviata al team");
    qc.invalidateQueries({ queryKey: ["portal", "feature-requests"] });
  };
  const submitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project) return;
    const { data: user } = await supabase.auth.getUser();
    await supabase.from("feature_requests").insert({
      project_id: project.id, requested_by: user.user!.id,
      title, description: desc, status: "requested",
    });
    toast.success("Idea inviata: riceverai un preventivo");
    setTitle(""); setDesc(""); setReqOpen(false);
    qc.invalidateQueries({ queryKey: ["portal", "feature-requests"] });
  };
  const approveQuote = async (id: string) => {
    await supabase.from("feature_requests").update({ status: "approved", approved_at: new Date().toISOString() }).eq("id", id);
    toast.success("Preventivo approvato");
    qc.invalidateQueries({ queryKey: ["portal", "feature-requests"] });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Area vantaggi</h1>
        <p className="text-white/60 mt-1">Extra premium disponibili · aggiungi al sito con un click.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(addons ?? []).map((a) => (
          <div key={a.id} className={`${glass} p-6 relative overflow-hidden`}>
            <div className="text-3xl">{a.icon}</div>
            <div className="mt-3 font-semibold">{a.name}</div>
            <p className="text-sm text-white/60 mt-1">{a.description}</p>
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm">
                <span className="text-white/50">da</span>{" "}
                <span className="font-semibold">{a.currency} {a.price_from}</span>
              </div>
              <button onClick={() => addAddon(a.name)} className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-black font-semibold flex items-center gap-1"><Plus className="h-3 w-3" /> Aggiungi</button>
            </div>
          </div>
        ))}
      </div>

      <div className={`${glass} p-6`}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-fuchsia-300" /><h2 className="font-semibold">Richieste nuove funzionalità</h2></div>
          <button onClick={() => setReqOpen((v) => !v)} className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15">Proponi un'idea</button>
        </div>
        {reqOpen && (
          <form onSubmit={submitRequest} className="space-y-2 mb-4">
            <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo della funzionalità" className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="Descrivi cosa vorresti aggiungere..." className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
            <button className="px-4 py-2 rounded-full bg-white text-black text-sm font-medium">Invia idea</button>
          </form>
        )}
        <div className="space-y-2">
          {(requests ?? []).map((r) => (
            <div key={r.id} className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-start gap-3">
              <span className={`mt-1 h-2 w-2 rounded-full ${r.status === "approved" ? "bg-emerald-400" : r.status === "quoted" ? "bg-amber-400" : "bg-white/30"}`} />
              <div className="flex-1">
                <div className="text-sm font-medium">{r.title}</div>
                {r.description && <div className="text-sm text-white/60 mt-0.5">{r.description}</div>}
                {r.quote_amount && (
                  <div className="mt-1 text-sm text-cyan-300">Preventivo: {r.quote_currency ?? "CHF"} {r.quote_amount}</div>
                )}
              </div>
              {r.quote_amount && r.status !== "approved" && (
                <button onClick={() => approveQuote(r.id)} className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Approva</button>
              )}
            </div>
          ))}
          {(requests ?? []).length === 0 && <div className="text-sm text-white/50 text-center py-4">Nessuna richiesta ancora.</div>}
        </div>
      </div>
    </div>
  );
}
